import {
  Wrench,
  Play,
  FileText,
  ThumbsUp,
  Activity,
  AlertTriangle,
} from 'lucide-react'
import type { ToolCatalogItem, DangerLevel } from '@/types/plugins'

interface ToolCardProps {
  tool: ToolCatalogItem
  onTryIt: (tool: ToolCatalogItem) => void
  onViewDocs?: (tool: ToolCatalogItem) => void
  compact?: boolean
}

const dangerColors: Record<DangerLevel, string> = {
  SAFE: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  LOW: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  MEDIUM: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  HIGH: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  CRITICAL: 'bg-red-500/10 text-red-400 border-red-500/20',
}

const dangerBorderColors: Record<DangerLevel, string> = {
  SAFE: 'border-emerald-500/20',
  LOW: 'border-blue-500/20',
  MEDIUM: 'border-yellow-500/20',
  HIGH: 'border-orange-500/20',
  CRITICAL: 'border-red-500/20',
}

function DangerBadge({ level }: { level: DangerLevel }) {
  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded border ${dangerColors[level]}`}
    >
      {level}
    </span>
  )
}

function SatisfactionBadge({ value }: { value: number | null }) {
  if (value === null) return null

  const percentage = Math.round(value * 100)
  const color =
    percentage >= 80
      ? 'text-emerald-400'
      : percentage >= 60
        ? 'text-yellow-400'
        : 'text-red-400'

  return (
    <span className={`text-xs font-mono flex items-center gap-0.5 ${color}`}>
      <ThumbsUp className="w-3 h-3" />
      {percentage}%
    </span>
  )
}

export default function ToolCard({
  tool,
  onTryIt,
  onViewDocs,
  compact = false,
}: ToolCardProps) {
  if (compact) {
    return (
      <div
        className={`bg-surface border rounded-lg p-3 hover:border-accent/30 transition-colors cursor-pointer ${dangerBorderColors[tool.dangerLevel]}`}
        onClick={() => onTryIt(tool)}
      >
        <div className="flex items-start gap-2">
          <Wrench className="w-4 h-4 text-text-muted mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-mono text-text truncate">
                {tool.name}
              </span>
              <DangerBadge level={tool.dangerLevel} />
            </div>
            <p className="text-xs text-text-muted truncate mt-0.5">
              {tool.description}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`bg-surface border rounded-xl p-4 hover:border-accent/30 transition-all hover:shadow-lg ${dangerBorderColors[tool.dangerLevel]}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Wrench className="w-4 h-4 text-accent flex-shrink-0" />
            <span className="text-sm font-mono text-text truncate">
              {tool.name}
            </span>
          </div>
          <p className="text-xs text-text-muted line-clamp-2">
            {tool.description}
          </p>
        </div>
        <DangerBadge level={tool.dangerLevel} />
      </div>

      {/* Stats */}
      <div className="flex items-center gap-3 text-xs text-text-muted mb-3">
        <span className="flex items-center gap-1">
          <Activity className="w-3 h-3" />
          {tool.totalCalls} calls
        </span>
        <SatisfactionBadge value={tool.satisfaction} />
        {tool.recentErrors > 0 && (
          <span className="flex items-center gap-1 text-red-400">
            <AlertTriangle className="w-3 h-3" />
            {tool.recentErrors} errors
          </span>
        )}
      </div>

      {/* Plugin tag */}
      <div className="text-[10px] text-text-secondary mb-3">
        Plugin: <span className="text-text-muted">{tool.plugin}</span>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onTryIt(tool)}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-accent/10 text-accent text-sm rounded-lg hover:bg-accent/20 transition-colors"
        >
          <Play className="w-3.5 h-3.5" />
          Try It
        </button>
        {onViewDocs && (
          <button
            onClick={() => onViewDocs(tool)}
            className="flex items-center justify-center gap-1.5 px-3 py-2 text-text-muted text-sm rounded-lg hover:bg-surface-hover border border-surface-border transition-colors"
          >
            <FileText className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  )
}
